import { ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { catchError } from "rxjs/operators";
import { MatDialog } from "@angular/material/dialog";
import { SimkitDialogComponent } from "../_core/components/simkit-dialog/simkit-dialog.component";
import { IdVerificationFailureDialogComponent} from "../_core/components/id-verification-failure-dailog/id-verification-failure-dialog.component"
import { Store } from "@ngrx/store";
import { AppState } from "app/_store/app.state";
import { MobileService } from "./mobile.service";
import { setCartProductName } from "app/_store";
import { setSimType } from "app/_store";
import {
	ButtonThemeState,
	OnDestroyedDirective,
	PRODUCT_NAME,
	SimNumberMask,
	trimWhiteSpaces,
} from "app/shared";
import { LengthValidator } from "@dodo/helper";
import { LocalStorageService } from "@dodo/local-storage";
import { LocalStorageKeys } from "app/_core/services/local-storage-state-restor.service";
import { TealiumService } from "tealium/services/tealium.service";
import { ErrorService } from "app/error/error.service";
import { SimStatusResponse } from "app/_core/_models/mobile/mobile.models";
import { Router } from "@angular/router";

@Component({
    selector: "dodo-mobile-activate",
    templateUrl: "./mobile-activate.component.html",
    styleUrls: ["./mobile-activate.component.scss"],
    standalone: false
})
export class MobileActivateComponent
	extends OnDestroyedDirective
	implements OnInit {
	public _form: FormGroup;
	public activateSimIsDisabled = false;
	public _replacementMap: [RegExp, string][] = [
		[/’/, `'`],
		[/\s+/, " "],
		[/^[ \t]+/, ""],
		[/[ \t]+$/, ""],
	];
	public _mask = SimNumberMask;
	public error = "";
	public busy = false;
	public buttonTheme = ButtonThemeState.GREEN;
	private sim: string;
	private simUseType = "";
	MSG_SIM_ALREADY_ACTIVATED =
		"This SIM number has already been activated. Please enter a different SIM number to continue. If you have any questions, please call us on <a href='tel:133636'>13 36 36</a>";
	MSG_SIM_ANY_OTHER_ERROR =
		"Check SIM number and try again. Still not working? Call us on <a href='tel:133636'>13 36 36</a>";
	MSG_SIM_AGNOSTIC =
		"We can see the SIM you have entered is no longer valid, if you wish to purchase a new plan/SIM go to: <a href='https://www.dodo.com/mobile'>dodo.com/mobile</a>";
	MAX_CHAR_COUNT = 99;
	customError = false;

	constructor(
		private fb: FormBuilder,
		private mobileService: MobileService,
		private cdr: ChangeDetectorRef,
		private store: Store<AppState>,
		private __tealiumService: TealiumService,
		private dialog: MatDialog,
		private localstorage: LocalStorageService,
		private __errorService: ErrorService,
		private router: Router,
	) {
		super();
		this.__errorService.checkMaintenace(PRODUCT_NAME.mobile);
	}

	ngOnInit(): void {
		const SimFromStorage = this.localstorage.getFromSessionStorage(
			LocalStorageKeys.sim
		);
		this.sim = SimFromStorage?.length > 0 ? SimFromStorage : null;

		this.store.dispatch(
			setCartProductName({ currentProduct: PRODUCT_NAME.mobile })
		);
		this.buildForm();
	}

	private buildForm(): void {
		this._form = this.fb.group({
			simNumber: [
				this.sim || "",
				[
					Validators.required,
					(simNumberValue) => LengthValidator(simNumberValue, 13),
				],
			],
		});
	}

	ngAfterViewInit(): void {
		this.cdr.detectChanges();
	}

	public openDialog(): void {
		this.dialog.open(SimkitDialogComponent, {
			autoFocus: false,
			panelClass: ["dodo-info-dialog"],
		});
	}
	
	public openIdVerificationFailureDialog(): void {
		this.dialog.open(IdVerificationFailureDialogComponent, {
			autoFocus: false,
			panelClass: ["dodo-info-dialog"],
		});
	}

	public formSubmit(): void {
		this.busy = true;
		this.error = "";
		const { simNumber } = this._form.value;
		const modifiedSimNumber = `896102${trimWhiteSpaces(simNumber)}`;

		this.mobileService
			.validateSim(modifiedSimNumber)
			.pipe(
				catchError((errs) => {
					if (
						errs?.error?.message === "SIM card is already activated"
					) {
						this.raiseError("SIM_ALREADY_ACTIVATED");
						 this.sendErrorToTealium(errs?.error?.code, errs?.error?.message, this.MSG_SIM_ALREADY_ACTIVATED);
					} else {
						this.raiseError(modifiedSimNumber);
						this.sendErrorToTealium(errs?.error?.code, errs?.error?.message);
					}
					throw errs;
				}),
				this.takeOne()
			)
			.subscribe((res$: any) => {
				if (!res$?.length) {
					this.raiseError(modifiedSimNumber);
					this.sendErrorToTealium();
				} else {
					const status = res$?.[0]?.product?.characteristic?.find(
						(c) => c?.name?.toLowerCase() === "status"
					)?.value;
					const useType = res$?.[0]?.product?.characteristic?.find(
						(c) => c?.name?.toLowerCase() === "usetype"
					)?.value;
					this.simUseType = useType;
					const isConverted = res$?.[0]?.product?.characteristic?.find(
						(c) => c?.name?.toLowerCase() === "leadisconverted"
					)?.value;
					const idCheckResponse = res$?.[0]?.idCheckResponse;
					const idKitVerification = res$?.[0]?.idKitVerification;

					if (
						isConverted === "true" ||
						status?.toLowerCase() === "inactive"
					) {
						this.raiseError();
						this.sendErrorToTealium();
					} else if (useType?.toLowerCase() === "generalv2") {
						// OPET-25407 - redirect to mydodo for non-agnostic SIMs
						window.location.href = "https://my.dodo.com/";
					} else if (res$?.[0]?.id !== "N/A" &&
						useType?.toLowerCase() !== "retail distribution" && isConverted !== "true") {
						this.raiseError("MSG_SIM_AGNOSTIC");
						this.sendErrorToTealium("", "", this.MSG_SIM_AGNOSTIC);
					} else if (
						res$?.[0]?.id !== "N/A" &&
						useType?.toLowerCase() !== "retail distribution") 
					{
						this.raiseError();
						this.sendErrorToTealium();
					} else if (idCheckResponse === 'DECLINE' && (idKitVerification === '' || idKitVerification === 'No response from the customer')) {
						this.activateSimIsDisabled = true;
						this.busy = false;
						this.router.navigate(['/id-verification']);
					} else if (idCheckResponse === 'DECLINE' && (idKitVerification === 'Fail' || idKitVerification === 'Flagged Fail')) {
						this.activateSimIsDisabled = true;
						this.busy = false;
						this.openIdVerificationFailureDialog();
					}
					else {
						this.mobileService
							.getSimStatus(trimWhiteSpaces(simNumber))
							.subscribe({
								next: (simStatus: SimStatusResponse) => {
								if ("Card is Active" === simStatus?.status) {
									const leadId = res$[0].id;
									const categoryName = res$[0].category.name;
									const product_sim_1P =
										leadId != null && categoryName != null;
									const product_sim_number = res$[0].product.characteristic.filter(
										(element) =>
											element.name === "serial number"
									)[0].value;
									const salesforce_lead_id = res$[0].product.characteristic.filter(
										(element) =>
											element.name == "serial number"
									)[0].id;									
									const promoStackingBehavior= res$[0].product.characteristic.filter(
										(element) =>{											
											return element?.name?.toLowerCase() === "promo stacking behavior"
										}
									)[0]?.value;								
									this.localstorage.setInSessionStorage(
										"leadId",
										leadId
									);
									this.localstorage.setInSessionStorage(
										"categoryName",
										categoryName
									);
									this.localstorage.setInSessionStorage(
										"product_sim_1P",
										product_sim_1P
									);
									this.localstorage.setInSessionStorage(
										"product_sim_number",
										product_sim_number
									);
									this.localstorage.setInSessionStorage(
										"salesforce_lead_id",
										salesforce_lead_id
									);
									this.localstorage.setInSessionStorage(
										"promoStackingBehavior",
										promoStackingBehavior
									);

									this.mobileService.processActivate(
										res$[0],
										simStatus,
										modifiedSimNumber,
										promoStackingBehavior
									);
								} else {
									this.raiseError();
									this.sendErrorToTealium();
								}
							},
							error: (error) => {
								console.error(error);
								this.raiseError();
								this.sendErrorToTealium(error?.error?.code, error?.error?.message);
							}
						});
					}
				}
			});
	}

	private raiseError(errorReason: string = "default"): void {
		const control = this._form?.get('simNumber');		
		if (errorReason === "SIM_ALREADY_ACTIVATED") {		
			this.error = this.MSG_SIM_ALREADY_ACTIVATED;
		} else if (errorReason === "MSG_SIM_AGNOSTIC") {			
			this.error = this.MSG_SIM_AGNOSTIC;
		} else {		
			this.error = this.MSG_SIM_ANY_OTHER_ERROR;
		}
		this.sendSimActivateMsgtoTealium(this.error);
		control?.setErrors({simActivateError:{
			message: this.error
		 }});
		control?.markAsTouched();
		this.busy = false;
	}

	private sendSimActivateMsgtoTealium(msgSIMtext): void {
		if (msgSIMtext.length >= this.MAX_CHAR_COUNT + 1) {
			msgSIMtext = msgSIMtext.substr(0, this.MAX_CHAR_COUNT) + "&hellip;";
		}
		this.__tealiumService.validateSim(msgSIMtext);
	}

	private sendErrorToTealium(errorCode: string = "", errorResponse: string = "", errorMessage: string = this.MSG_SIM_ANY_OTHER_ERROR) {
		this.__tealiumService.trackPageEvent({
			event_trigger: "error",
			type: "system error", 
			error_code: errorCode,
			error_message: errorMessage,
			error_response: errorResponse,
			sim_use_type: this.simUseType,
		 });
	}
}
